import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import LoadingSpinner from './LoadingSpinner';
import { discordFetch } from '../../utils/discordApi';

interface DailyAverageScoreProps {
  className?: string;
  mode?: 'text' | 'picture';
}

const DailyAverageScore: React.FC<DailyAverageScoreProps> = ({ className = '', mode = 'text' }) => {
  const [dailyAverageScore, setDailyAverageScore] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDailyAverageScore = async () => {
      setIsLoading(true);
      try {
        const endpoint = mode === 'picture'
          ? API_ENDPOINTS.DAILY_AVERAGE_SCORE_PICTURE_TODAY
          : API_ENDPOINTS.DAILY_AVERAGE_SCORE_TODAY;

        const response = await discordFetch(endpoint);
        if (response.ok) {
          // Check content type before parsing JSON
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            setDailyAverageScore(data.averageScore);
          } else {
            console.warn('Daily average score API returned non-JSON response');
            setDailyAverageScore(null);
          }
        } else {
          setDailyAverageScore(null);
        }
      } catch (error) {
        console.error('Error fetching daily average score:', {
          error: error,
          message: error instanceof Error ? error.message : String(error),
          mode
        });

        setDailyAverageScore(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDailyAverageScore();
  }, [mode]);

  if (isLoading) {
    return (
      <div className={`inline-block bg-gradient-to-br from-dark-800 to-dark-900 border ${mode === 'picture' ? 'border-secondary-500/50' : 'border-primary-500/50'} rounded-lg p-2 sm:p-4 shadow-neon relative overflow-hidden ${className}`}>
        {/* Corner accents - smaller on mobile */}
        <div className={`absolute top-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-t-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-primary-400'} opacity-60`}></div>
        <div className={`absolute top-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-t-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-primary-400'} opacity-60`}></div>
        <div className={`absolute bottom-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-b-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-primary-400'} opacity-60`}></div>
        <div className={`absolute bottom-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-b-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-primary-400'} opacity-60`}></div>

        <div className="text-center relative z-10 flex justify-center items-center min-h-[80px] min-w-[100px]">
          <LoadingSpinner size="small" message="Loading" />
        </div>
      </div>
    );
  }

  if (dailyAverageScore === null) {
    return (
      <div className={`inline-block bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-600 rounded-lg p-2 sm:p-4 shadow-none relative overflow-hidden ${className}`}>
        {/* Corner accents - smaller on mobile */}
        <div className="absolute top-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-t-2 border-dark-500 opacity-60"></div>
        <div className="absolute top-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-t-2 border-dark-500 opacity-60"></div>
        <div className="absolute bottom-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-b-2 border-dark-500 opacity-60"></div>
        <div className="absolute bottom-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-b-2 border-dark-500 opacity-60"></div>

        <div className="text-center relative z-10">
          <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-dark-400 rounded-full"></div>
            <div className="text-xs text-dark-300 font-mono tracking-wider uppercase">
              Avg Score
            </div>
          </div>
          <div className="text-lg sm:text-2xl font-display font-bold text-dark-400 mb-1">0.0</div>
          <div className="text-xs text-dark-500 font-mono tracking-wider uppercase hidden sm:block">No Data</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-block bg-gradient-to-br from-dark-800 to-dark-900 border ${mode === 'picture' ? 'border-secondary-500/50' : 'border-accent-500/50'} rounded-lg p-2 sm:p-4 shadow-[0_0_10px_rgba(34,197,94,0.3)] relative overflow-hidden ${className}`}>
      {/* Corner accents - smaller on mobile */}
      <div className={`absolute top-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-t-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-accent-400'} opacity-60`}></div>
      <div className={`absolute top-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-t-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-accent-400'} opacity-60`}></div>
      <div className={`absolute bottom-1 left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-b-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-accent-400'} opacity-60`}></div>
      <div className={`absolute bottom-1 right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-b-2 ${mode === 'picture' ? 'border-secondary-400' : 'border-accent-400'} opacity-60`}></div>

      {/* Scanning line effect */}
      <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent ${mode === 'picture' ? 'via-secondary-400' : 'via-accent-400'} to-transparent animate-scan opacity-50`}></div>

      <div className="text-center relative z-10">
        <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
          <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${mode === 'picture' ? 'bg-secondary-400' : 'bg-accent-400'} rounded-full animate-pulse`}></div>
          <div className={`text-xs ${mode === 'picture' ? 'text-secondary-300' : 'text-accent-300'} font-mono tracking-wider uppercase`}>
            Avg Score
          </div>
        </div>
        <div className={`text-lg sm:text-2xl font-display font-bold ${mode === 'picture' ? 'text-secondary-400' : 'text-accent-400'} mb-1`}>{dailyAverageScore.toFixed(1)}</div>
        <div className="text-xs text-dark-400 font-mono tracking-wider uppercase hidden sm:block">Correct Answers</div>
      </div>
    </div>
  );
};

export default DailyAverageScore;